// Datenzugriff der Lead-Pipeline (ERP/CRM-Plan, Abschnitt 4).
//
// Unrein: hier liegen ausschliesslich Supabase-Aufrufe und das
// snake_case<->camelCase-Mapping (Stil wie crmService.ts: async, throw bei
// error, Mapper oben). Die Fachlichkeit — erlaubte Statusuebergaenge,
// Priorisierung, Erstkontakt-Kanal, Kunden-Vorbelegung — steht pur und
// getestet in leadLogic.ts und wird von hier nur AUFGERUFEN, nicht
// nachgebaut.
//
// Tabellen + RLS: supabase/crm_leads.sql (MIGRATIONS.md Nr. 16).

import { supabase } from './supabaseClient';
import { Customer } from '../types';
import { createCustomer } from './crmService';
import { describeError } from './errorMessage';
import { ladeAlleSeiten } from './seitenweise';
import {
    LeadStatus,
    canTransition,
    leadToCustomerDraft,
    timestampsForStatus,
    transitionHindernis,
} from './leadLogic';

// ─── Typen ───────────────────────────────────────────────────────────────────

/** Ein Lead in camelCase (Spalten in supabase/crm_leads.sql). */
export interface Lead {
    id: string;
    companyId: string;
    name: string;
    branche?: string;
    street?: string;
    zip?: string;
    city?: string;
    bundesland?: string;
    lat?: number;
    lng?: number;
    website?: string;
    email?: string;
    phone?: string;
    ansprechpartner?: string;
    /** 'scraper:<source>' | 'manuell' | 'empfehlung' | 'ausschreibung'. */
    quelle: string;
    quelleUrl?: string;
    status: LeadStatus;
    lostReason?: string;
    score?: number;
    scoreBegruendung?: string;
    ownerUserId?: string;
    nextActionAt?: string;
    qualifiedAt?: string;
    wonAt?: string;
    lostAt?: string;
    customerId?: string;
    notes?: string;
    createdAt: string;
    updatedAt: string;
}

export type LeadActivityTyp = 'anruf' | 'email' | 'termin' | 'notiz' | 'wiedervorlage';

export interface LeadActivity {
    id: string;
    companyId: string;
    leadId?: string;
    customerId?: string;
    typ: LeadActivityTyp;
    betreff?: string;
    inhalt?: string;
    faelligAm?: string;
    erledigt: boolean;
    userId?: string;
    createdAt: string;
    /** Nur bei listOffeneWiedervorlagen befuellt (Join auf leads.name). */
    leadName?: string;
}

export interface LeadStatusLogEntry {
    id: string;
    leadId: string;
    oldStatus?: string;
    newStatus: string;
    userId?: string;
    createdAt: string;
}

export const LEAD_ACTIVITY_LABELS: Record<LeadActivityTyp, string> = {
    anruf: 'Anruf',
    email: 'E-Mail',
    termin: 'Termin',
    notiz: 'Notiz',
    wiedervorlage: 'Wiedervorlage',
};

// ─── Row-Mapper ──────────────────────────────────────────────────────────────

const mapLeadRow = (row: any): Lead => ({
    id: row.id,
    companyId: row.company_id,
    name: row.name,
    branche: row.branche ?? undefined,
    street: row.street ?? undefined,
    zip: row.zip ?? undefined,
    city: row.city ?? undefined,
    bundesland: row.bundesland ?? undefined,
    lat: row.lat != null ? Number(row.lat) : undefined,
    lng: row.lng != null ? Number(row.lng) : undefined,
    website: row.website ?? undefined,
    email: row.email ?? undefined,
    phone: row.phone ?? undefined,
    ansprechpartner: row.ansprechpartner ?? undefined,
    quelle: row.quelle,
    quelleUrl: row.quelle_url ?? undefined,
    status: row.status,
    lostReason: row.lost_reason ?? undefined,
    score: row.score != null ? Number(row.score) : undefined,
    scoreBegruendung: row.score_begruendung ?? undefined,
    ownerUserId: row.owner_user_id ?? undefined,
    nextActionAt: row.next_action_at ?? undefined,
    qualifiedAt: row.qualified_at ?? undefined,
    wonAt: row.won_at ?? undefined,
    lostAt: row.lost_at ?? undefined,
    customerId: row.customer_id ?? undefined,
    notes: row.notes ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
});

/**
 * Nur DEFINIERTE Felder in die Row uebernehmen, damit updateLead mit Partial<>
 * keine Spalte versehentlich auf null setzt (gleiche Regel wie toCustomerRow).
 */
const toLeadRow = (l: Partial<Lead>): Record<string, any> => {
    const row: Record<string, any> = {};
    if (l.companyId !== undefined) row.company_id = l.companyId;
    if (l.name !== undefined) row.name = l.name;
    if (l.branche !== undefined) row.branche = l.branche || null;
    if (l.street !== undefined) row.street = l.street || null;
    if (l.zip !== undefined) row.zip = l.zip || null;
    if (l.city !== undefined) row.city = l.city || null;
    if (l.bundesland !== undefined) row.bundesland = l.bundesland || null;
    // Koordinaten: ein bewusst geleertes Feld muss null werden — die Zahl 0
    // schriebe sonst den Atlantik in die Karte (Muster toCustomerRow).
    if (l.lat !== undefined) row.lat = Number.isFinite(l.lat) ? l.lat : null;
    if (l.lng !== undefined) row.lng = Number.isFinite(l.lng) ? l.lng : null;
    if (l.website !== undefined) row.website = l.website || null;
    if (l.email !== undefined) row.email = l.email || null;
    if (l.phone !== undefined) row.phone = l.phone || null;
    if (l.ansprechpartner !== undefined) row.ansprechpartner = l.ansprechpartner || null;
    if (l.quelle !== undefined) row.quelle = l.quelle;
    if (l.quelleUrl !== undefined) row.quelle_url = l.quelleUrl || null;
    if (l.status !== undefined) row.status = l.status;
    if (l.lostReason !== undefined) row.lost_reason = l.lostReason || null;
    if (l.score !== undefined) row.score = Number.isFinite(l.score) ? l.score : null;
    if (l.scoreBegruendung !== undefined) row.score_begruendung = l.scoreBegruendung || null;
    if (l.ownerUserId !== undefined) row.owner_user_id = l.ownerUserId || null;
    if (l.nextActionAt !== undefined) row.next_action_at = l.nextActionAt || null;
    if (l.qualifiedAt !== undefined) row.qualified_at = l.qualifiedAt || null;
    if (l.wonAt !== undefined) row.won_at = l.wonAt || null;
    if (l.lostAt !== undefined) row.lost_at = l.lostAt || null;
    if (l.customerId !== undefined) row.customer_id = l.customerId || null;
    if (l.notes !== undefined) row.notes = l.notes || null;
    return row;
};

const mapActivityRow = (row: any): LeadActivity => ({
    id: row.id,
    companyId: row.company_id,
    leadId: row.lead_id ?? undefined,
    customerId: row.customer_id ?? undefined,
    typ: row.typ,
    betreff: row.betreff ?? undefined,
    inhalt: row.inhalt ?? undefined,
    faelligAm: row.faellig_am ?? undefined,
    erledigt: !!row.erledigt,
    userId: row.user_id ?? undefined,
    createdAt: row.created_at,
    leadName: row.leads?.name ?? undefined,
});

const mapStatusLogRow = (row: any): LeadStatusLogEntry => ({
    id: row.id,
    leadId: row.lead_id,
    oldStatus: row.old_status ?? undefined,
    newStatus: row.new_status,
    userId: row.user_id ?? undefined,
    createdAt: row.created_at,
});

// ─── Mandant ─────────────────────────────────────────────────────────────────

/**
 * Die company_id des angemeldeten Profils — jeder INSERT braucht sie
 * (`company_id not null references companies(id)`).
 *
 * Bewusst clientseitig ermittelt und NICHT aus einer RPC: die App trennt
 * Mandanten durchgehend so (companyScope.ts), und eine Sonderlocke nur fuer
 * Leads waere inkonsistent. Fehlt sie, ist das Profil nicht zugeordnet
 * (companies.sql / MIGRATIONS.md Nr. 9 noch nicht gefahren) — dann bricht die
 * Anlage hier mit einer Erklaerung ab statt spaeter mit einem FK-Fehler.
 */
export const currentCompanyId = async (): Promise<string> => {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) throw new Error('Nicht angemeldet — Leads lassen sich nicht speichern.');

    const { data, error } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', userId)
        .maybeSingle();
    if (error) throw new Error(describeError(error));

    const companyId = (data as any)?.company_id;
    if (!companyId) {
        throw new Error(
            'Das eigene Profil hat keine Firmenzuordnung (company_id). ' +
            'Bitte die Migration companies.sql ausführen bzw. das Profil zuordnen — ' +
            'ohne Mandant lassen sich keine Leads anlegen.',
        );
    }
    return String(companyId);
};

// ─── Leads: CRUD ─────────────────────────────────────────────────────────────

export interface LeadQuery {
    /** Nur diese Status. Leer/fehlend = alle. */
    status?: LeadStatus[];
    branche?: string;
    bundesland?: string;
    /** Praefix-Vergleich, damit 'scraper:' die ganze Familie trifft. */
    quellePrefix?: string;
    ownerUserId?: string;
}

// Seitenweise: der Lead-Scout legt schnell Tausende an, und PostgREST kappt
// ohne `.range()` still bei 1000 (Vorfall 23.10.2026, Kundenliste 1000 von 1244).
export const fetchLeads = async (q: LeadQuery = {}): Promise<Lead[]> => {
    const rows = await ladeAlleSeiten<Record<string, unknown>>((von, bis) => {
        let query = supabase.from('leads').select('*');
        if (q.status?.length) query = query.in('status', q.status);
        if (q.branche) query = query.eq('branche', q.branche);
        if (q.bundesland) query = query.eq('bundesland', q.bundesland);
        if (q.ownerUserId) query = query.eq('owner_user_id', q.ownerUserId);
        if (q.quellePrefix) query = query.like('quelle', `${q.quellePrefix}%`);
        return query.order('updated_at', { ascending: false }).order('id').range(von, bis);
    }).catch((error: unknown) => {
        throw new Error(describeError(error));
    });
    return rows.map(mapLeadRow);
};

export const fetchLead = async (id: string): Promise<Lead | null> => {
    const { data, error } = await supabase.from('leads').select('*').eq('id', id).maybeSingle();
    if (error) throw new Error(describeError(error));
    return data ? mapLeadRow(data) : null;
};

export type NewLead = Omit<Lead, 'id' | 'companyId' | 'status' | 'createdAt' | 'updatedAt'> &
    Partial<Pick<Lead, 'status' | 'companyId'>>;

export const createLead = async (lead: NewLead): Promise<Lead> => {
    const companyId = lead.companyId || (await currentCompanyId());
    const row = toLeadRow({ ...lead, companyId, status: lead.status ?? 'neu' });
    const { data, error } = await supabase.from('leads').insert([row]).select('*').single();
    if (error) throw new Error(describeError(error));
    return mapLeadRow(data);
};

/**
 * Stammdaten aendern. Der STATUS gehoert bewusst NICHT hierher — dafuer gibt es
 * setLeadStatus, das den Uebergang prueft und die Zeitstempel setzt. Kaeme er
 * hier durch, liesse sich die Pipeline mit einem beliebigen Wert ueberspringen.
 */
export const updateLead = async (id: string, updates: Partial<Lead>): Promise<void> => {
    const { status: _status, ...rest } = updates;
    const row = toLeadRow(rest);
    if (!Object.keys(row).length) return;
    row.updated_at = new Date().toISOString();
    const { error } = await supabase.from('leads').update(row).eq('id', id);
    if (error) throw new Error(describeError(error));
};

export interface StatusChangeOptions {
    /** Pflicht bei 'verloren' — ohne Grund ist die Absage nicht auswertbar. */
    lostReason?: string;
    /** Nur fuer 'gewonnen' relevant; wird sonst ignoriert. */
    customerId?: string;
}

/**
 * Statuswechsel mit Uebergangspruefung und Zeitstempeln.
 *
 * Die Pruefung laeuft VOR dem Schreiben gegen den frisch gelesenen Status,
 * nicht gegen den, den die Oberflaeche noch im Speicher hat: zwischen
 * Kartenanzeige und Klick kann ein Kollege den Lead bewegt haben.
 */
export const setLeadStatus = async (
    leadId: string,
    to: LeadStatus,
    opts: StatusChangeOptions = {},
): Promise<Lead> => {
    const lead = await fetchLead(leadId);
    if (!lead) throw new Error('Lead nicht gefunden — er wurde vermutlich gelöscht.');

    const customerId = opts.customerId || lead.customerId;
    const hindernis = transitionHindernis(lead.status, to, { customerId });
    if (hindernis) throw new Error(hindernis);

    if (to === 'verloren' && !(opts.lostReason || '').trim()) {
        throw new Error('Bitte einen Grund angeben — ohne ihn ist die Absage später nicht auswertbar.');
    }

    const nowIso = new Date().toISOString();
    const stamps = timestampsForStatus(to, nowIso);
    const row: Record<string, any> = {
        ...toLeadRow(stamps as Partial<Lead>),
        status: to,
        updated_at: nowIso,
    };
    if (to === 'verloren') row.lost_reason = (opts.lostReason || '').trim();
    if (to === 'gewonnen' && customerId) row.customer_id = customerId;

    // Der Filter auf den gelesenen Status macht den Wechsel atomar: zwei
    // gleichzeitige Klicks koennen nicht beide gewinnen (Muster acceptQuote).
    const { data, error } = await supabase
        .from('leads')
        .update(row)
        .eq('id', leadId)
        .eq('status', lead.status)
        .select('*');
    if (error) throw new Error(describeError(error));
    if (!data?.length) {
        throw new Error('Der Lead wurde zwischenzeitlich von jemand anderem verschoben — bitte neu laden.');
    }
    return mapLeadRow(data[0]);
};

export const deleteLead = async (id: string): Promise<void> => {
    const { error } = await supabase.from('leads').delete().eq('id', id);
    if (error) throw new Error(describeError(error));
};

/** Statushistorie eines Leads (vom Trigger geschrieben, nur lesbar). */
export const fetchLeadStatusLog = async (leadId: string): Promise<LeadStatusLogEntry[]> => {
    const { data, error } = await supabase
        .from('lead_status_log')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapStatusLogRow);
};

// ─── Aktivitaeten ────────────────────────────────────────────────────────────

export const fetchLeadActivities = async (leadId: string): Promise<LeadActivity[]> => {
    const { data, error } = await supabase
        .from('lead_activities')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapActivityRow);
};

export type NewLeadActivity = Omit<LeadActivity, 'id' | 'companyId' | 'createdAt' | 'erledigt' | 'leadName'> &
    Partial<Pick<LeadActivity, 'erledigt' | 'companyId'>>;

export const createLeadActivity = async (a: NewLeadActivity): Promise<LeadActivity> => {
    if (!a.leadId && !a.customerId) {
        throw new Error('Eine Aktivität braucht einen Bezug — Lead oder Kunde.');
    }
    const companyId = a.companyId || (await currentCompanyId());
    const { data: auth } = await supabase.auth.getUser();

    const row: Record<string, any> = {
        company_id: companyId,
        lead_id: a.leadId ?? null,
        customer_id: a.customerId ?? null,
        typ: a.typ,
        betreff: a.betreff || null,
        inhalt: a.inhalt || null,
        faellig_am: a.faelligAm || null,
        erledigt: !!a.erledigt,
        user_id: auth?.user?.id ?? null,
    };
    const { data, error } = await supabase.from('lead_activities').insert([row]).select('*').single();
    if (error) throw new Error(describeError(error));
    return mapActivityRow(data);
};

export const setActivityDone = async (id: string, erledigt = true): Promise<void> => {
    const { error } = await supabase.from('lead_activities').update({ erledigt }).eq('id', id);
    if (error) throw new Error(describeError(error));
};

export const deleteLeadActivity = async (id: string): Promise<void> => {
    const { error } = await supabase.from('lead_activities').delete().eq('id', id);
    if (error) throw new Error(describeError(error));
};

/**
 * Faellige, offene Wiedervorlagen — die Zahl am „Leads"-Reiter und der
 * Abschnitt oben in der Liste.
 *
 * BEWUSST NICHT im globalen Aufgaben-/Benachrichtigungssystem: dort haengen
 * Zaehler und Glocke an Projekten, und ein Vertriebs-Nachfass ist keine
 * Projektaufgabe. Er wuerde die Projektzahlen verfaelschen und liefe ausserdem
 * an Leads ohne Projekt ins Leere.
 *
 * `todayIso` wird uebergeben statt hier gebildet: so ist der Stichtag
 * derselbe wie in der Oberflaeche und der Aufruf bleibt vorhersagbar.
 */
export const listOffeneWiedervorlagen = async (
    todayIso: string = new Date().toLocaleDateString('sv-SE'),
): Promise<LeadActivity[]> => {
    const { data, error } = await supabase
        .from('lead_activities')
        .select('*, leads(name)')
        .eq('erledigt', false)
        .not('faellig_am', 'is', null)
        .lte('faellig_am', todayIso.slice(0, 10))
        .order('faellig_am', { ascending: true });
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapActivityRow);
};

// ─── Lead -> Kunde ───────────────────────────────────────────────────────────

/**
 * Die Vorbelegung der Kundenanlage. Reine Ableitung — hier nur
 * weitergereicht, damit die Oberflaeche nur EINEN Service kennen muss.
 */
export { leadToCustomerDraft } from './leadLogic';

/**
 * Konvertierung: aus dem Lead wird ein Kunde.
 *
 * Ablauf und seine Begruendung:
 *  1. Kunde anlegen (createCustomer zieht die Kundennummer ueber die RPC —
 *     der Nummernkreis darf nicht umgangen werden).
 *  2. Am Lead customer_id setzen. ERST DANN
 *  3. Status auf 'gewonnen' — setLeadStatus prueft den Uebergang, und der
 *     CHECK `leads_won_needs_customer` in der Datenbank verlangt die
 *     customer_id bereits. Umgekehrte Reihenfolge scheiterte am Constraint.
 *
 * Bricht Schritt 3 ab, bleibt der Kunde bestehen und der Lead traegt bereits
 * seine customer_id: die Konvertierung ist dann nachvollziehbar halbfertig und
 * per Klick abschliessbar — besser als ein zurueckgerollter Kunde, dessen
 * Nummer verbrannt waere.
 *
 * `overrides` traegt die im Dialog geaenderten Felder (Name korrigiert,
 * Branche nachgepflegt).
 */
export const convertLeadToCustomer = async (
    lead: Lead,
    overrides: Partial<Customer> = {},
): Promise<{ customer: Customer; lead: Lead }> => {
    if (lead.customerId) {
        throw new Error('Dieser Lead ist bereits mit einem Kunden verknüpft.');
    }
    const draft = leadToCustomerDraft(lead);
    const name = (overrides.name ?? draft.name).trim();
    if (!name) throw new Error('Ohne Namen lässt sich kein Kunde anlegen.');

    const customer = await createCustomer({
        ...draft,
        ...overrides,
        name,
        contacts: overrides.contacts ?? [],
        paymentTermsDays: overrides.paymentTermsDays ?? 14,
        defaultDiscountPercent: overrides.defaultDiscountPercent ?? 0,
    } as Omit<Customer, 'id' | 'createdBy' | 'createdAt' | 'updatedAt'>);

    await updateLead(lead.id, { customerId: customer.id });

    // Der Weg zu 'gewonnen' fuehrt ueber 'angebot'. Steht der Lead noch
    // frueher, waere setLeadStatus zu Recht gescheitert — die Konvertierung
    // selbst ist aber der fachliche Beleg des Erfolgs. Deshalb wird der
    // Endstatus direkt gesetzt, wenn der regulaere Uebergang nicht greift, und
    // der Sprung ist durch lead_status_log dokumentiert.
    const nowIso = new Date().toISOString();
    if (canTransition(lead.status, 'gewonnen', { customerId: customer.id })) {
        const updated = await setLeadStatus(lead.id, 'gewonnen', { customerId: customer.id });
        return { customer, lead: updated };
    }

    const { data, error } = await supabase
        .from('leads')
        .update({ status: 'gewonnen', won_at: nowIso, customer_id: customer.id, updated_at: nowIso })
        .eq('id', lead.id)
        .select('*')
        .single();
    if (error) throw new Error(describeError(error));
    return { customer, lead: mapLeadRow(data) };
};

// ─── Aktive Projekte als Bezugspunkte der Priorisierung ─────────────────────

/**
 * Koordinaten der laufenden Projekte — Eingabe fuer
 * leadLogic.computeLeadPriority.
 *
 * Nur der Koordinaten-Pfad aus dem Blob: das ganze `data` waren auf PROD
 * 16 MB fuer 610 Projekte (23.10.2026), gebraucht werden zwei Zahlen. Als
 * Objekt (`->`), nicht als `->>lat`: ein fehlendes lat wuerde sonst zu
 * Text-null und ueber Number() zu 0. Projekte ohne Koordinaten fallen heraus.
 *
 * Seitenweise: projects waechst auf die stille 1000er-Grenze von PostgREST zu.
 */
export const fetchActiveProjectPoints = async (): Promise<{ id: string; name: string; lat: number; lng: number }[]> => {
    type ProjektPunktZeile = {
        id: unknown;
        name?: unknown;
        koordinaten?: { lat?: unknown; lng?: unknown } | null;
    };
    const rows = await ladeAlleSeiten<ProjektPunktZeile>((von, bis) =>
        supabase
            .from('projects')
            .select('id, name, koordinaten:data->metadata->globalData->locationCoordinates')
            .neq('status', 'archived')
            .order('id')
            .range(von, bis))
        .catch((error: unknown) => {
            throw new Error(describeError(error));
        });

    const out: { id: string; name: string; lat: number; lng: number }[] = [];
    for (const row of rows) {
        const coords = row?.koordinaten;
        const lat = Number(coords?.lat);
        const lng = Number(coords?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
        if (lat === 0 && lng === 0) continue;
        out.push({ id: String(row.id), name: String(row.name || ''), lat, lng });
    }
    return out;
};
